/**
 * Consistent Dialog Styling System
 *
 * Apply these styles to ALL dialogs/modals for consistency. Since the
 * September 2026 cohesion pass this IS the entry-sheet pattern: a bottom
 * sheet on phones and a 12px card on larger screens, the family's cyan
 * accent line along the top, a monospace uppercase title, the standard cyan
 * close button, and one primary action next to an outlined secondary.
 *
 * global.css sizes every `.MuiDialog-paper` with `!important` (8px margin and
 * `height: auto` under 768px), so the contested properties are written under
 * `&&` to double the specificity — same trick as design-system/sheetPaperSx.
 */

import { entryFormSx } from '../components/design-system/entryFormSx';

const ACCENT_LINE = {
  content: '""',
  position: 'absolute' as const,
  top: 0,
  left: 16,
  right: 16,
  height: '2px',
  background: '#00E0FF',
  boxShadow: '0 0 12px #00E0FF, 0 0 4px #00E0FF',
  zIndex: 2,
};

export const DIALOG_STYLES = {
  // Standard dialog props for Material-UI Dialog
  standard: {
    maxWidth: 'sm' as const,
    fullWidth: true,
    scroll: 'paper' as const,
    PaperProps: {
      sx: {
        background: 'var(--bg-card)',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        '&::before': ACCENT_LINE,
        '&&': {
          border: '1px solid rgba(0,224,255,0.32)',
          borderRadius: { xs: '18px 18px 0 0', sm: '12px' },
          borderBottom: { xs: 'none', sm: '1px solid rgba(0,224,255,0.2)' },
          margin: { xs: '0 !important', sm: '32px' },
          width: { xs: '100vw !important', sm: '100%' },
          maxWidth: { xs: '100vw !important', sm: '560px' },
          maxHeight: { xs: 'calc(var(--vh, 1vh) * 88) !important', sm: 'calc(85vh)' },
          paddingBottom: { xs: 'env(safe-area-inset-bottom, 0px)', sm: 0 },
        },
      },
    },
    // Phones: pin the sheet to the bottom edge.
    sx: {
      '& .MuiDialog-container': { alignItems: { xs: 'flex-end', sm: 'center' } },
    },
    BackdropProps: {
      sx: {
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        backdropFilter: 'blur(4px)',
      },
    },
  },

  // Dialog title styling — the title text inside becomes the family's
  // monospace uppercase header whatever element the caller used.
  title: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    px: 2,
    py: 1.5,
    borderBottom: '1px solid rgba(0,224,255,0.15)',
    background: 'var(--bg-card)',
    '& .MuiTypography-root': {
      fontFamily: 'var(--font-mono)',
      fontWeight: 700,
      fontSize: '0.8rem',
      letterSpacing: 2,
      textTransform: 'uppercase' as const,
      color: '#00E0FF',
      lineHeight: 1.3,
    },
  },

  // Close button styling (always top-right) — the app's standard cyan circle
  closeButton: {
    color: '#00E0FF',
    bgcolor: 'rgba(0,224,255,0.1)',
    border: '1px solid rgba(0,224,255,0.2)',
    '&:hover': {
      bgcolor: 'rgba(0,224,255,0.18)',
    },
  },

  // A <form> wrapped around content + actions (react-hook-form dialogs) must
  // stay a flex column, or it — not the content — becomes the overflow and the
  // whole sheet scrolls, taking the title with it (the quick-add-panel bug).
  form: {
    display: 'flex' as const,
    flexDirection: 'column' as const,
    flex: '1 1 auto',
    minHeight: 0,
  },

  // Dialog content styling
  content: {
    p: 2,
    backgroundColor: 'var(--bg-card)',
    overflowY: 'auto' as const,
    flex: '1 1 auto',
    minHeight: 0,
    overscrollBehavior: 'contain' as const,
    // Every field inside a dialog takes the entry-form look (label above, pill field, mono value).
    ...entryFormSx,
  },

  // Dialog actions footer styling
  actions: {
    flexShrink: 0,
    px: 2,
    py: 1.5,
    borderTop: '1px solid rgba(0,224,255,0.15)',
    backgroundColor: 'var(--bg-card)',
    gap: 1,
  },

  // Primary action button
  primaryButton: {
    minWidth: '100px',
    fontWeight: 600,
  },

  // Secondary action button — outlined cyan, whatever variant the theme
  // would otherwise force (the theme sets `border: none` on every button).
  secondaryButton: {
    minWidth: '100px',
    background: 'transparent !important',
    border: '1px solid rgba(0,224,255,0.5) !important',
    color: '#00E0FF !important',
    '&:hover': { border: '1px solid #00E0FF !important', backgroundColor: 'rgba(0,224,255,0.1) !important' },
  },
};
